<template>
  <section class="fuse-seq" ref="sectionRef">
    <div class="fuse-seq-inner">
      <div class="fuse-seq-sections">
        <button
          v-for="(sec, i) in sections"
          :key="i"
          class="fuse-seq-sections-btn"
          :class="{ active: activeSectionIndex === i }"
          @click="jumpToSection(i)"
        >
          {{ sec.label }}
        </button>
      </div>

      <div class="fuse-seq-stage">
        <div class="fuse-seq-label" :class="{ visible: labelVisible }">
          <span class="fuse-seq-label-text">{{ activeSection.label }}</span>
        </div>
        <div class="fuse-seq-canvas-wrap">
          <canvas ref="canvasA" class="fuse-seq-canvas active" />
          <canvas ref="canvasB" class="fuse-seq-canvas" />
        </div>
        <button
          v-if="playbackDone && !isPlaying"
          class="fuse-seq-replay"
          @click="replay"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="1 4 1 10 7 10"/>
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
          </svg>
          <span>Replay</span>
        </button>

        <div
          v-show="scrubberVisible"
          class="fuse-seq-scrubber"
          ref="trackRef"
          @mousedown.prevent="startDrag"
          @touchstart.prevent="startDrag"
        >
          <div class="fuse-seq-scrubber-track" ref="innerTrackRef">
            <div
              v-for="(sec, i) in sections"
              :key="i"
              class="fuse-seq-scrubber-segment"
              :style="{
                left: sec.startPct + '%',
                width: (sec.endPct - sec.startPct) + '%'
              }"
              :class="{ active: activeSectionIndex === i }"
            />
            <div
              v-for="(sec, i) in sections.slice(1)"
              :key="'m' + i"
              class="fuse-seq-scrubber-marker"
              :style="{ left: sec.startPct + '%' }"
            />
            <div
              class="fuse-seq-scrubber-fill"
              :style="{ width: progress + '%' }"
            />
            <div
              class="fuse-seq-scrubber-thumb"
              :style="{ left: progress + '%' }"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'

// Import all 38 frames
import f1 from '@/assets/img/fuse-sequence/frame-1.jpg'
import f2 from '@/assets/img/fuse-sequence/frame-2.jpg'
import f3 from '@/assets/img/fuse-sequence/frame-3.jpg'
import f4 from '@/assets/img/fuse-sequence/frame-4.jpg'
import f5 from '@/assets/img/fuse-sequence/frame-5.jpg'
import f6 from '@/assets/img/fuse-sequence/frame-6.jpg'
import f7 from '@/assets/img/fuse-sequence/frame-7.jpg'
import f8 from '@/assets/img/fuse-sequence/frame-8.jpg'
import f9 from '@/assets/img/fuse-sequence/frame-9.jpg'
import f10 from '@/assets/img/fuse-sequence/frame-10.jpg'
import f11 from '@/assets/img/fuse-sequence/frame-11.jpg'
import f12 from '@/assets/img/fuse-sequence/frame-12.jpg'
import f13 from '@/assets/img/fuse-sequence/frame-13.jpg'
import f14 from '@/assets/img/fuse-sequence/frame-14.jpg'
import f15 from '@/assets/img/fuse-sequence/frame-15.jpg'
import f16 from '@/assets/img/fuse-sequence/frame-16.jpg'
import f17 from '@/assets/img/fuse-sequence/frame-17.jpg'
import f18 from '@/assets/img/fuse-sequence/frame-18.jpg'
import f19 from '@/assets/img/fuse-sequence/frame-19.jpg'
import f20 from '@/assets/img/fuse-sequence/frame-20.jpg'
import f21 from '@/assets/img/fuse-sequence/frame-21.jpg'
import f22 from '@/assets/img/fuse-sequence/frame-22.jpg'
import f23 from '@/assets/img/fuse-sequence/frame-23.jpg'
import f24 from '@/assets/img/fuse-sequence/frame-24.jpg'
import f25 from '@/assets/img/fuse-sequence/frame-25.jpg'
import f26 from '@/assets/img/fuse-sequence/frame-26.jpg'
import f27 from '@/assets/img/fuse-sequence/frame-27.jpg'
import f28 from '@/assets/img/fuse-sequence/frame-28.jpg'
import f29 from '@/assets/img/fuse-sequence/frame-29.jpg'
import f30 from '@/assets/img/fuse-sequence/frame-30.jpg'
import f31 from '@/assets/img/fuse-sequence/frame-31.jpg'
import f32 from '@/assets/img/fuse-sequence/frame-32.jpg'
import f33 from '@/assets/img/fuse-sequence/frame-33.jpg'
import f34 from '@/assets/img/fuse-sequence/frame-34.jpg'
import f35 from '@/assets/img/fuse-sequence/frame-35.jpg'
import f36 from '@/assets/img/fuse-sequence/frame-36.jpg'
import f37 from '@/assets/img/fuse-sequence/frame-37.jpg'
import f38 from '@/assets/img/fuse-sequence/frame-38.jpg'
import f39 from '@/assets/img/fuse-sequence/frame-39.jpg'
import f40 from '@/assets/img/fuse-sequence/frame-40.jpg'
import f41 from '@/assets/img/fuse-sequence/frame-41.jpg'
import f42 from '@/assets/img/fuse-sequence/frame-42.jpg'
import f43 from '@/assets/img/fuse-sequence/frame-43.jpg'
import f44 from '@/assets/img/fuse-sequence/frame-44.jpg'
import f45 from '@/assets/img/fuse-sequence/frame-45.jpg'
import f46 from '@/assets/img/fuse-sequence/frame-46.jpg'
import f47 from '@/assets/img/fuse-sequence/frame-47.jpg'
import f48 from '@/assets/img/fuse-sequence/frame-48.jpg'
import f49 from '@/assets/img/fuse-sequence/frame-49.jpg'
import f50 from '@/assets/img/fuse-sequence/frame-50.jpg'

const frameSrcs = [
  f1,f2,f3,f4,f5,f6,f7,f8,f9,f10,
  f11,f12,f13,f14,f15,f16,f17,f18,f19,f20,
  f21,f22,f23,f24,f25,f26,f27,f28,
  f29,f30,f31,f32,f33,f34,f35,f36,f37,f38,
  f39,f40,f41,f42,f43,f44,f45,f46,f47,f48,f49,f50
]

const TOTAL = frameSrcs.length
const FRAME_INTERVAL = 200 // ms per frame during auto-play
const CROSSFADE_MS = 300 // crossfade duration between frames (longer overlap = smoother)
const SECTION_PAUSE = 1000 // ms pause between sections

const sections = [
  { label: 'Fuse Edging', start: 0, end: 9 },
  { label: 'Curvilinear Planters', start: 10, end: 27 },
  { label: 'Linear Planters', start: 28, end: 37 },
  { label: 'Planting Trays', start: 38, end: 49 }
].map(s => ({
  ...s,
  startPct: (s.start / (TOTAL - 1)) * 100,
  endPct: (s.end / (TOTAL - 1)) * 100
}))

defineProps(['data'])

const sectionRef = ref(null)
const canvasA = ref(null)
const canvasB = ref(null)
const trackRef = ref(null)
const innerTrackRef = ref(null)
const progress = ref(0)
const labelVisible = ref(false)
const isPlaying = ref(false)
const playbackDone = ref(false)
const hasAutoPlayed = ref(false)
const scrubberVisible = ref(false)

const images = []
let currentFrame = 0
let frontCanvas = 'A' // which canvas is currently visible
let labelTimeout = null
let playTimer = null
let observer = null

const activeSectionIndex = computed(() => {
  const frame = Math.round((progress.value / 100) * (TOTAL - 1))
  for (let i = sections.length - 1; i >= 0; i--) {
    if (frame >= sections[i].start) return i
  }
  return 0
})

const activeSection = computed(() => sections[activeSectionIndex.value])

function getCanvases() {
  return { a: canvasA.value, b: canvasB.value }
}

function drawOnCanvas(cvs, index) {
  if (!cvs) return
  const ctx = cvs.getContext('2d')
  const img = images[index]
  if (!ctx || !img) return
  ctx.clearRect(0, 0, cvs.width, cvs.height)
  ctx.drawImage(img, 0, 0, cvs.width, cvs.height)
}

function drawFrameImmediate(index) {
  // Draw on front canvas without crossfade (for scrubbing / initial draw)
  const { a, b } = getCanvases()
  const front = frontCanvas === 'A' ? a : b
  drawOnCanvas(front, index)
}

function crossfadeTo(index) {
  const { a, b } = getCanvases()
  if (!a || !b) return

  const back = frontCanvas === 'A' ? b : a
  const front = frontCanvas === 'A' ? a : b

  // Draw new frame on the back canvas and make it visible instantly
  drawOnCanvas(back, index)
  back.style.transition = 'none'
  back.style.opacity = '1'

  // Force layout so the instant opacity applies before transition starts
  void back.offsetHeight

  // Fade old frame out to reveal new one underneath — no white flash
  front.style.transition = `opacity ${CROSSFADE_MS}ms ease`
  front.style.opacity = '0'

  // Swap which is "front"
  frontCanvas = frontCanvas === 'A' ? 'B' : 'A'
}

function resizeCanvas() {
  const { a, b } = getCanvases()
  if (!a || !b || !images[0]) return
  const parent = a.parentElement
  const displayW = parent.clientWidth
  const aspect = images[0].naturalWidth / images[0].naturalHeight
  const renderW = Math.min(images[0].naturalWidth, displayW * (window.devicePixelRatio || 1))

  for (const cvs of [a, b]) {
    cvs.width = renderW
    cvs.height = renderW / aspect
  }

  drawFrameImmediate(currentFrame)
}

function preloadImages() {
  return new Promise((resolve) => {
    let loaded = 0
    frameSrcs.forEach((src, i) => {
      const img = new Image()
      img.src = src
      img.onload = () => {
        loaded++
        if (loaded === TOTAL) resolve()
      }
      images[i] = img
    })
  })
}

function setFrame(idx, smooth = false) {
  if (idx === currentFrame) return
  currentFrame = idx
  progress.value = (idx / (TOTAL - 1)) * 100

  if (smooth) {
    crossfadeTo(idx)
  } else {
    drawFrameImmediate(idx)
  }
}

function showLabel(duration = 1500) {
  labelVisible.value = true
  clearTimeout(labelTimeout)
  if (duration > 0) {
    labelTimeout = setTimeout(() => { labelVisible.value = false }, duration)
  }
}

function hideLabel() {
  clearTimeout(labelTimeout)
  labelVisible.value = false
}

// --- Auto-play ---
function autoPlay(startIdx = 0) {
  isPlaying.value = true
  playbackDone.value = false
  let idx = startIdx

  setFrame(idx, false)
  showLabel(0) // stay visible (no auto-hide)

  const sectionEnds = new Set(sections.map(s => s.end))

  function step() {
    if (!isPlaying.value) return

    idx++
    if (idx >= TOTAL) {
      isPlaying.value = false
      playbackDone.value = true
      scrubberVisible.value = true
      showLabel(2000) // show final section label briefly then fade
      return
    }

    setFrame(idx, true)

    if (sectionEnds.has(idx) && idx < TOTAL - 1) {
      playTimer = setTimeout(() => {
        showLabel(0) // keep label visible for new section
        playTimer = setTimeout(step, SECTION_PAUSE)
      }, FRAME_INTERVAL)
    } else {
      playTimer = setTimeout(step, FRAME_INTERVAL)
    }
  }

  playTimer = setTimeout(step, 800)
}

function stopPlayback() {
  isPlaying.value = false
  clearTimeout(playTimer)
}

function replay() {
  // Reset both canvases
  const { a, b } = getCanvases()
  if (a) { a.style.transition = 'none'; a.style.opacity = '1' }
  if (b) { b.style.transition = 'none'; b.style.opacity = '0' }
  frontCanvas = 'A'
  currentFrame = -1
  setFrame(0, false)
  autoPlay(0)
}

// --- Scrubber drag ---
function updateFrame(pct) {
  progress.value = pct
  const idx = Math.round((pct / 100) * (TOTAL - 1))
  // During scrubbing, draw immediately on front canvas (no crossfade)
  const { a, b } = getCanvases()
  const front = frontCanvas === 'A' ? a : b
  const back = frontCanvas === 'A' ? b : a
  if (front) { front.style.transition = 'none'; front.style.opacity = '1' }
  if (back) { back.style.transition = 'none'; back.style.opacity = '0' }
  setFrame(idx, false)
}

function getProgress(e) {
  const track = innerTrackRef.value
  if (!track) return 0
  const rect = track.getBoundingClientRect()
  const clientX = e.touches ? e.touches[0].clientX : e.clientX
  return Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100))
}

function startDrag(e) {
  stopPlayback()
  playbackDone.value = true
  updateFrame(getProgress(e))

  const onMove = (ev) => {
    ev.preventDefault()
    updateFrame(getProgress(ev))
  }
  const onEnd = () => {
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mouseup', onEnd)
    window.removeEventListener('touchmove', onMove)
    window.removeEventListener('touchend', onEnd)
  }
  window.addEventListener('mousemove', onMove, { passive: false })
  window.addEventListener('mouseup', onEnd)
  window.addEventListener('touchmove', onMove, { passive: false })
  window.addEventListener('touchend', onEnd)
}

function jumpToSection(idx) {
  stopPlayback()
  scrubberVisible.value = true

  const sec = sections[idx]

  // Reset canvases cleanly before playing
  const { a, b } = getCanvases()
  if (a) { a.style.transition = 'none'; a.style.opacity = '1' }
  if (b) { b.style.transition = 'none'; b.style.opacity = '0' }
  frontCanvas = 'A'

  currentFrame = -1
  setFrame(sec.start, false)
  playbackDone.value = false
  isPlaying.value = true
  showLabel(0) // keep visible for entire section

  let frame = sec.start

  function step() {
    if (!isPlaying.value) return

    frame++
    if (frame > sec.end) {
      isPlaying.value = false
      playbackDone.value = true
      showLabel(2000) // fade out after section ends
      return
    }

    setFrame(frame, true)
    playTimer = setTimeout(step, FRAME_INTERVAL)
  }

  playTimer = setTimeout(step, 800)
}

// --- IntersectionObserver ---
function setupObserver() {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasAutoPlayed.value) {
          hasAutoPlayed.value = true
          autoPlay(0)
        }
      })
    },
    { threshold: 0.4 }
  )
  if (sectionRef.value) observer.observe(sectionRef.value)
}

onMounted(async () => {
  await preloadImages()
  resizeCanvas()
  drawFrameImmediate(0)
  // Ensure canvas A is visible, B is hidden
  const { a, b } = getCanvases()
  if (a) a.style.opacity = '1'
  if (b) b.style.opacity = '0'
  window.addEventListener('resize', resizeCanvas)
  setupObserver()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeCanvas)
  clearTimeout(labelTimeout)
  clearTimeout(playTimer)
  if (observer) observer.disconnect()
})
</script>

<style lang="scss" scoped>
.fuse-seq {
  @apply relative;

  &-inner {
    @apply w-full max-w-screen-xl mx-auto
    px-[10px] py-[40px]
    md:px-[60px] md:py-[80px]
    xl:px-0 xl:py-[100px];
  }

  &-sections {
    @apply flex gap-2 mb-5 flex-wrap;

    &-btn {
      @apply px-4 py-2 rounded-full text-[13px] font-medium cursor-pointer;
      border: 1px solid rgba(0, 0, 0, 0.12);
      background: transparent;
      color: #666;
      transition: all 0.2s ease;

      @screen md {
        font-size: 14px;
        padding: 8px 20px;
      }

      &:hover {
        border-color: rgba(0, 0, 0, 0.3);
        color: #333;
      }

      &.active {
        background: #1a1a1a;
        border-color: #1a1a1a;
        color: #fff;
      }
    }
  }

  &-stage {
    @apply relative;
  }

  &-label {
    @apply absolute top-4 left-4 z-10 px-4 py-2 rounded-lg;
    background: rgba(0, 0, 0, 0.65);
    backdrop-filter: blur(8px);
    opacity: 0;
    transform: translateY(-4px);
    transition: opacity 0.3s ease, transform 0.3s ease;
    pointer-events: none;

    @screen md {
      top: 24px;
      left: 24px;
      padding: 10px 20px;
    }

    &.visible {
      opacity: 1;
      transform: translateY(0);
    }

    &-text {
      @apply text-white font-medium;
      font-size: 14px;

      @screen md {
        font-size: 18px;
      }
    }
  }

  &-canvas-wrap {
    @apply w-full overflow-hidden relative;
    border-radius: 16px;
    background: #f0f0f0;
  }

  &-canvas {
    @apply block;
    width: 100%;
    height: auto;

    &:nth-child(2) {
      @apply absolute inset-0;
      opacity: 0;
    }
  }

  &-replay {
    @apply absolute flex items-center gap-2 cursor-pointer;
    bottom: 40px;
    right: 20px;
    z-index: 10;
    background: rgba(0, 0, 0, 0.65);
    backdrop-filter: blur(8px);
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: 8px;
    border: none;
    transition: background 0.2s ease, transform 0.15s ease;

    @screen md {
      bottom: 48px;
      right: 24px;
      font-size: 15px;
      padding: 10px 20px;
    }

    &:hover {
      background: rgba(0, 0, 0, 0.8);
      transform: scale(1.03);
    }

    svg {
      opacity: 0.9;
    }
  }

  &-scrubber {
    @apply absolute left-0 right-0 bottom-0 cursor-pointer;
    height: 32px;
    display: flex;
    align-items: flex-end;
    touch-action: none;
    z-index: 5;
    padding: 0 16px;

    @screen md {
      padding: 0 24px;
    }

    &-track {
      @apply absolute left-4 right-4;
      bottom: 10px;
      height: 4px;
      background: rgba(255, 255, 255, 0.3);
      border-radius: 4px;

      @screen md {
        left: 24px;
        right: 24px;
      }
    }

    &-segment {
      position: absolute;
      top: 0;
      height: 100%;
      background: rgba(255, 255, 255, 0.1);
      transition: background 0.2s ease;

      &.active {
        background: rgba(255, 255, 255, 0.15);
      }
    }

    &-marker {
      position: absolute;
      top: -4px;
      width: 2px;
      height: 12px;
      background: rgba(255, 255, 255, 0.4);
      transform: translateX(-50%);
      border-radius: 1px;
      z-index: 2;
    }

    &-fill {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      background: #E8773A;
      border-radius: 4px;
      pointer-events: none;
      z-index: 1;
    }

    &-thumb {
      position: absolute;
      top: 50%;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #E8773A;
      border: 2px solid #fff;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
      transform: translate(-50%, -50%);
      pointer-events: none;
      z-index: 3;
      transition: transform 0.15s ease;
    }

    &:hover &-thumb,
    &:active &-thumb {
      transform: translate(-50%, -50%) scale(1.2);
    }
  }
}
</style>
